import * as R from 'ramda';
import { handleActions } from 'redux-actions';

import * as commonReducers from './common';
import * as actions from '../actions/contacts';

import { getUpdatedIds } from './helpers';
import { CONTACTS_TYPE_FILTERS } from '../../api/constants/index';
import { isNeededResetDataSearch, areArraysEqual } from '../../utils';

const {
  fetch,
  create,
  remove,
  update,
} = commonReducers.operationTypes;

const contactWithDefaultValues = R.mergeDeepRight({ requests: [] });

const getFormattedContacts = (state, contacts) =>
  Object.keys(contacts).reduce((acc, key) => {
    const {
      requests,
      threadsCount,
      contactRequests,
    } = R.pathOr({}, ['items', key], state);

    const newContact = {
      ...(requests ? { requests } : {}),
      ...(threadsCount ? { threadsCount } : {}),
      ...(contactRequests ? { contactRequests } : {}),
      ...contacts[key],
    };

    return R.assoc(key, newContact, acc);
  }, {});


const fetchContactSuccessReducer = (state, { payload }) => ({
  ...state,
  items: {
    ...state.items,
    [payload._id]: payload,
  },
});

const fetchContactsSuccessReducer = (
  state,
  {
    payload: {
      ids, contacts = {}, existsMore, notNewPage = false, count, polling = false,
    } = {},
  },
) => ({
  ...state,
  [fetch]: false,
  count: R.isNil(count) ? state.count : count,
  page: notNewPage ? state.page : state.page + 1,
  existsMore: R.isNil(existsMore) ? state.existsMore : existsMore,
  ids: getUpdatedIds(state, ids, polling),
  items: { ...state.items, ...getFormattedContacts(state, contacts) },
});

const searchContactsSuccessReducer = (
  state,
  {
    payload: {
      contacts = {}, existsMore, ids, count = 0,
    } = {},
  },
) => ({
  ...state,
  count,
  [fetch]: false,
  page: state.page + 1,
  existsMore: R.isNil(existsMore) ? state.existsMore : existsMore,
  ids: R.isNil(ids) ? state.ids : R.union(state.ids, ids),
  items: { ...state.items, ...getFormattedContacts(state, contacts) },
});

const fetchContactThreadsSuccessReducer = (
  state,
  {
    payload: {
      threads: newThreads, contactId, page, count: totalThreadsCount,
    } = {},
  },
) => ({
  ...state,
  [fetch]: false,
  threads: { ...state.threads, [contactId]: newThreads },
  currentThreadsPage: page,
  totalThreadsCount,
  threadPages: {},
});

const fetchContactThreadMessagesSuccessReducer = (
  state,
  { payload: { thread, page, contactId } = {} },
) => {
  const stateCopy = { ...state };
  let contactThreads = stateCopy.threads[contactId];

  const findedThreadIndex = R.findIndex(R.propEq('_id', thread._id))(contactThreads);

  contactThreads = R.remove(findedThreadIndex, 1, contactThreads);
  contactThreads = R.insert(findedThreadIndex, { ...thread }, contactThreads);

  return {
    ...state,
    isThreadOpening: false,
    threadPages: {
      ...state.threadPages,
      [thread._id]: +page,
    },
    threads: {
      ...state.threads,
      [contactId]: [...contactThreads],
    },
  };
};

const createContactReducer = (state, { payload }) => ({
  ...state,
  [create]: false,
  count: state.count + 1,
  ids: R.union(state.ids, [payload._id]),
  items: R.assoc(payload._id, contactWithDefaultValues(payload), state.items),
});

const updateContactReducer = (state, { payload }) => ({
  ...state,
  [update]: false,
  items: {
    ...state.items,
    [payload._id]: {
      ...state.items[payload._id],
      ...payload,
    },
  },
});

const updateContactRequestsReducer = (state, {
  payload: { requestId, oldRequestContactsIds, newRequestContactsIds },
}) => {
  // if Request's Contacts were changed
  if (!areArraysEqual(oldRequestContactsIds, newRequestContactsIds)) {
    const commonContacts = R.intersection(oldRequestContactsIds, newRequestContactsIds);

    let updatedContacts = {};

    const oldContacts = R.filter(i => !R.contains(i, commonContacts), oldRequestContactsIds);

    // remove Request from Contacts that were unassigned from it
    oldContacts.forEach((id) => {
      updatedContacts = {
        ...updatedContacts,
        [id]: {
          ...R.path(['items', id], state),
          requests: R.without([requestId], R.pathOr([], ['items', id, 'requests'], state)),
        },
      };
    });

    const newContacts = R.filter(i => !R.contains(i, commonContacts), newRequestContactsIds);

    // add Request to Contacts that were assigned to it
    newContacts.forEach((id) => {
      updatedContacts = {
        ...updatedContacts,
        [id]: {
          ...R.path(['items', id], state),
          requests: R.union([requestId], R.pathOr([], ['items', id, 'requests'], state)),
        },
      };
    });

    return {
      ...state,
      items: { ...state.items, ...updatedContacts },
    };
  }

  return state;
};

const deleteContactReducer = (state, { payload }) => ({
  ...state,
  [remove]: false,
  count: state.count - 1,
  ids: R.without([payload], state.ids),
  items: R.dissoc(payload, state.items),
});

const deleteContactThreadSuccessReducer = (state, { payload }) => {
  const { contactId, threadId } = payload;

  return {
    ...state,
    threads: {
      ...state.threads,
      [contactId]: R.remove(
        R.findIndex(R.propEq('_id', threadId))(state.threads[contactId]),
        1,
        state.threads[contactId],
      ),
    },
  };
};

const resetSearchDataReducer = (
  state,
  { payload: { prevSearchTerm = '', searchTerm = '' } = {} },
) => ({
  ...state,
  ...(isNeededResetDataSearch(prevSearchTerm, searchTerm)
    ? {
      ids: [],
      page: 0,
      count: 0,
      existsMore: false,
      openContactCard: null,
      openRequestCard: null,
      openContactThread: {},
    }
    : {}),
  [fetch]: false,
});

const setNewFilter = (state, { payload: { filterType, data } }) => ({
  ...state,
  ids: [],
  page: 0,
  count: 0,
  openContactCard: null,
  openRequestCard: null,
  openContactThread: {},
  filters: {
    ...state.filters,
    [filterType]: data,
  },
  existsMore: false,
});

const resetFiltersReducer = state => ({
  ...state,
  ids: [],
  page: 0,
  count: 0,
  filters: {
    type: CONTACTS_TYPE_FILTERS.ALL.value,
    countries: [],
  },
  existsMore: false,
});

export const contactsReducers = handleActions(
  {
    // create
    [actions.createContactRequest]: commonReducers.commonRequestReducer(create),
    [actions.createContactFailure]: commonReducers.commonFailureReducer(create),
    [actions.createContactSuccess]: createContactReducer,

    // update
    [actions.updateContactRequest]: commonReducers.commonRequestReducer(update),
    [actions.updateContactFailure]: commonReducers.commonFailureReducer(update),
    [actions.updateContactSuccess]: updateContactReducer,

    // delete
    [actions.deleteContactRequest]: commonReducers.commonRequestReducer(remove),
    [actions.deleteContactFailure]: commonReducers.commonFailureReducer(remove),
    [actions.deleteContactSuccess]: deleteContactReducer,

    // fetch
    [actions.fetchContactsRequest]: commonReducers.commonRequestReducer(fetch),
    [actions.fetchContactsFailure]: commonReducers.commonFailureReducer(fetch),
    [actions.fetchContactsSuccess]: fetchContactsSuccessReducer,

    // search
    [actions.searchContactsRequest]: commonReducers.commonRequestReducer(fetch),
    [actions.searchContactsFailure]: R.mergeDeepLeft({ isFetching: false, ids: [] }),
    [actions.searchContactsSuccess]: searchContactsSuccessReducer,

    [actions.fetchContactSuccess]: fetchContactSuccessReducer,

    // fetch contact's threads
    [actions.fetchContactThreadsRequest]: commonReducers.commonRequestReducer(fetch),
    [actions.fetchContactThreadsFailure]: commonReducers.commonFailureReducer(fetch),
    [actions.fetchContactThreadsSuccess]: fetchContactThreadsSuccessReducer,

    // fetch contact's thread's messages
    [actions.fetchContactThreadMessagesRequest]: R.mergeDeepLeft({ isThreadOpening: true }),
    [actions.fetchContactThreadMessagesFailure]: R.mergeDeepLeft({ isThreadOpening: false }),
    [actions.fetchContactThreadMessagesSuccess]: fetchContactThreadMessagesSuccessReducer,

    [actions.deleteContactThreadSuccess]: deleteContactThreadSuccessReducer,

    [actions.resetThreadPages]: state => R.assoc('threadPages', {}, state),

    [actions.updateContactRequests]: updateContactRequestsReducer,

    [actions.setOpenContact]: (state, { payload }) => R.assoc('openContactCard', payload, state),
    [actions.setOpenContactRequest]: (state, { payload }) => R.assoc('openRequestCard', payload, state),

    [actions.setOpenContactThread]: (state, { payload }) => R.mergeDeepLeft(
      { openContactThread: payload },
      state,
    ),

    [actions.setPage]: commonReducers.commonSetPageReducer,

    [actions.setFilter]: setNewFilter,
    [actions.resetFilters]: resetFiltersReducer,

    [actions.resetSearchDataContacts]: resetSearchDataReducer,

  },
  {
    ids: [],
    threads: {},
    existsMore: true,
    isThreadOpening: false,
    openContactCard: null,
    openRequestCard: null,
    openContactThread: { event: 'click' },
    threadPages: {},
    page: 0,
    count: 0,
    filters: {
      type: CONTACTS_TYPE_FILTERS.ALL.value,
      countries: [],
    },
    ...commonReducers.defaultState,
  },
);



// WEBPACK FOOTER //
// ./src/redux/reducers/contacts.js